import { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { IconExternalLink, IconPackage, IconPlus } from "@tabler/icons-react";
import { useCaseSendOuts } from "../../hooks/useSendOuts";
import {
  SendOut,
  SendoutRequestType,
  SendoutStatus,
} from "../../services/sendOuts.service";
import SendOutDetailModal from "../send-outs/SendOutDetailModal";
import { ExternalOpinionSendOutCard } from "./ExternalOpinionSendOutCard";

// ==================== TYPES ====================

interface ExternalOpinionCenterProps {
  caseId: string | null;
  onRequestExternalOpinion: () => void;
  isReadOnly?: boolean;
}

// ==================== HELPERS ====================

function filterExternalOpinionSendOuts(sendOuts: SendOut[]): {
  active: SendOut[];
  history: SendOut[];
} {
  // External opinion send-outs have no stainProviderId
  const externalOpinion = sendOuts.filter(
    (sendOut) =>
      !sendOut.stainProviderId &&
      sendOut.requestType !== SendoutRequestType.MATERIAL_RETURN
  );

  const active = externalOpinion.filter(
    (so) =>
      so.status !== SendoutStatus.REJECTED &&
      so.status !== SendoutStatus.RECEIVED
  );

  const history = externalOpinion.filter(
    (so) =>
      so.status === SendoutStatus.REJECTED ||
      so.status === SendoutStatus.RECEIVED
  );

  return { active, history };
}

// ==================== COMPONENT ====================

export function ExternalOpinionCenter({
  caseId,
  onRequestExternalOpinion,
  isReadOnly = false,
}: ExternalOpinionCenterProps) {
  const [selectedSendOutId, setSelectedSendOutId] = useState<string | null>(
    null
  );

  const { data: sendOuts = [], isLoading } = useCaseSendOuts(
    caseId ?? undefined
  );

  const { active, history } = useMemo(
    () => filterExternalOpinionSendOuts(sendOuts),
    [sendOuts]
  );
  const totalCount = useMemo(
    () => active.length + history.length,
    [active.length, history.length]
  );

  const handleSendOutClick = useCallback((sendOutId: string) => {
    setSelectedSendOutId(sendOutId);
  }, []);

  const handleDetailClose = useCallback(() => {
    setSelectedSendOutId(null);
  }, []);

  if (!caseId) {
    return (
      <Box>
        <Text c="dimmed" ta="center" size="sm" py="xl">
          Save the case first to view external opinion requests
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconExternalLink size={20} color="var(--mantine-color-violet-6)" />
          <Text size="lg" fw={600}>
            External Opinion
          </Text>
          {totalCount > 0 && (
            <Badge size="sm" variant="light" color="violet">
              {totalCount}
            </Badge>
          )}
        </Group>
        {!isReadOnly && (
          <Button
            size="sm"
            color="violet"
            leftSection={<IconPlus size={14} />}
            onClick={onRequestExternalOpinion}
          >
            Request External Opinion
          </Button>
        )}
      </Group>

      <Divider />

      {/* List */}
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : totalCount === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconPackage size={40} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed" size="sm">
              No external opinion requests for this case
            </Text>
            {!isReadOnly && (
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconPlus size={14} />}
                onClick={onRequestExternalOpinion}
              >
                Request External Opinion
              </Button>
            )}
          </Stack>
        </Center>
      ) : (
        <Stack gap="md">
          {/* Active send-outs */}
          {active.length > 0 && (
            <Stack gap="sm">
              <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                Active ({active.length})
              </Text>
              {active.map((sendOut) => (
                <ExternalOpinionSendOutCard
                  key={sendOut.id}
                  sendOut={sendOut}
                  onClick={handleSendOutClick}
                />
              ))}
            </Stack>
          )}

          {/* History */}
          {history.length > 0 && (
            <Stack gap="sm">
              <Divider
                label={
                  <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                    History ({history.length})
                  </Text>
                }
                labelPosition="left"
              />
              {history.map((sendOut) => (
                <ExternalOpinionSendOutCard
                  key={sendOut.id}
                  sendOut={sendOut}
                  onClick={handleSendOutClick}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {/* Send-out detail modal */}
      <SendOutDetailModal
        opened={!!selectedSendOutId}
        onClose={handleDetailClose}
        sendOutId={selectedSendOutId}
      />
    </Stack>
  );
}
